import { useState, useEffect } from 'react';

// Image cache utility for faster loading and better performance
interface CachedImage {
  url: string;
  blob: Blob;
  timestamp: number;
  objectUrl: string;
}

class ImageCache {
  private cache = new Map<string, CachedImage>();
  private readonly maxAge = 24 * 60 * 60 * 1000; // 24 hours
  private readonly maxSize = 50; // Maximum number of cached images

  async get(url: string): Promise<string> {
    try {
      // Check if image is already cached and not expired
      const cached = this.cache.get(url);
      if (cached && Date.now() - cached.timestamp < this.maxAge) {
        return cached.objectUrl;
      }

      // Fetch image with caching headers
      const response = await fetch(url, {
        headers: {
          'Cache-Control': 'public, max-age=86400', // Cache for 24 hours
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status}`);
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);

      // Clean up old cache if at max size
      if (this.cache.size >= this.maxSize) {
        this.cleanup();
      }

      // Store in cache
      const cacheEntry: CachedImage = {
        url,
        blob,
        timestamp: Date.now(),
        objectUrl
      };

      // Clean up previous object URL if it exists
      if (cached) {
        URL.revokeObjectURL(cached.objectUrl);
      }

      this.cache.set(url, cacheEntry);
      return objectUrl;

    } catch (error) {
      console.warn('Image cache failed for:', url, error);
      // Return original URL if caching fails
      return url;
    }
  }

  preload(urls: string[]): void {
    // Validate input
    if (!Array.isArray(urls)) {
      console.warn('preload expects an array of URLs, received:', typeof urls);
      return;
    }

    // Preload images in background without blocking UI
    urls.forEach(url => {
      if (typeof url === 'string' && !this.cache.has(url)) {
        this.get(url).catch(() => {
          // Silently fail preload attempts
        });
      }
    });
  }

  private cleanup(): void {
    // Remove oldest entries when cache is full
    const entries = Array.from(this.cache.entries());
    entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
    
    // Remove oldest 25% of entries
    const removeCount = Math.floor(this.maxSize * 0.25);
    for (let i = 0; i < removeCount; i++) {
      const [url, cached] = entries[i];
      URL.revokeObjectURL(cached.objectUrl);
      this.cache.delete(url);
    }
  }

  clear(): void {
    // Clean up all object URLs and clear cache
    this.cache.forEach(cached => {
      URL.revokeObjectURL(cached.objectUrl);
    });
    this.cache.clear();
  }

  getStats(): { size: number; maxSize: number; totalUrls: string[] } {
    return {
      size: this.cache.size,
      maxSize: this.maxSize,
      totalUrls: Array.from(this.cache.keys())
    };
  }
}

// Singleton instance
export const imageCache = new ImageCache();

// React hook for cached images
export const useCachedImage = (url: string | undefined): string | undefined => {
  const [cachedUrl, setCachedUrl] = useState<string | undefined>(url);

  useEffect(() => {
    if (!url) {
      setCachedUrl(undefined);
      return;
    }

    let isCancelled = false;

    const loadImage = async () => {
      try {
        const cached = await imageCache.get(url);
        if (!isCancelled) {
          setCachedUrl(cached);
        }
      } catch (error) {
        console.warn('Failed to cache image:', url, error);
        if (!isCancelled) {
          setCachedUrl(url); // Fallback to original URL
        }
      }
    };

    loadImage();

    return () => {
      isCancelled = true;
    };
  }, [url]);

  return cachedUrl;
};

// Cleanup on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    imageCache.clear();
  });
}

export default imageCache;